
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {
  cidReferences, InlineImageSource, LOADING_IMAGE_SRC, markCidImages, MISSING_IMAGE_SRC, resolveInlineImages,
} from '@/services/helpdeskEmailImages';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

@Component({name: 'EmailHtmlFrame'})
export default class EmailHtmlFrame extends Vue {
  @Prop({default: ''}) html!: string;
  @Prop({default: 15}) fontSize!: number;
  @Prop({default: '#263238'}) color!: string;
  // Clamp for compact previews (overflow is hidden, like the previous max-height boxes).
  @Prop({default: null}) maxHeight!: number | null;
  // Lets wide content (600px newsletters) scroll sideways inside the frame on narrow screens.
  @Prop({default: false}) scrollX!: boolean;
  // Which email the HTML belongs to, so its `cid:` images can be read from Gmail.
  // Without it (templates, signatures) those images can't exist and show as unavailable.
  @Prop({default: ''}) inlineSource!: InlineImageSource | '';
  @Prop({default: null}) inlineId!: number | null;

  height = 40;
  // True while maxHeight hides part of the content (emitted as `clamped` for "show more" toggles).
  clamped = false;
  private resizeObserver: any = null;
  // cid -> blob URL (null = not recoverable). Kept out of `srcdoc`: images are swapped into the
  // loaded document, so the email shows right away and the frame never reloads for them.
  private inlineUrls: {[cid: string]: string | null} = {};
  private inlineRequest = 0;

  get canResolveInline(): boolean {
    return !!(this.inlineSource && this.inlineId);
  }

  @Watch('html', {immediate: true})
  @Watch('inlineSource')
  @Watch('inlineId')
  onInlineContextChanged() {
    const request = ++this.inlineRequest;
    this.inlineUrls = {};
    const cids = cidReferences(this.html);
    if (!cids.length || !this.canResolveInline) return;

    resolveInlineImages(this.inlineSource as InlineImageSource, this.inlineId as number, cids).then((urls) => {
      if (request !== this.inlineRequest) return;
      this.inlineUrls = urls;
      this.applyInlineImages();
    });
  }

  get srcdoc(): string {
    const raw = markCidImages(this.html || '', this.canResolveInline ? LOADING_IMAGE_SRC : MISSING_IMAGE_SRC);
    // Plain-text emails keep their line breaks.
    const content = /<[a-z!/][\s\S]*>/i.test(raw)
      ? raw
      : '<div style="white-space:pre-wrap">' + escapeHtml(raw) + '</div>';

    const base = [
      '<base target="_blank">',
      '<style>',
      'html,body{margin:0;padding:0;}',
      'body{font-family:Roboto,-apple-system,"Segoe UI",Arial,sans-serif;',
      'font-size:' + this.fontSize + 'px;line-height:1.6;color:' + this.color + ';',
      'overflow-wrap:anywhere;overflow-y:hidden;overflow-x:' + (this.scrollX ? 'auto' : 'hidden') + ';}',
      'img{max-width:100%;height:auto;}',
      'table{max-width:100%;}',
      'pre{white-space:pre-wrap;}',
      // Signatures pasted from Gmail drag its UI along (width:1656px wrappers, position:fixed panels,
      // a Meet iframe...). Neutralise layout-only patterns so they render aligned with the message.
      'div,section,article,span,p{max-width:100% !important;}',
      '[style*="position: fixed"],[style*="position:fixed"],[style*="position: absolute"],[style*="position:absolute"]{position:static !important;}',
      '[style*="min-height: 100%"],[style*="min-height:100%"]{min-height:0 !important;}',
      'iframe,object,embed,video,audio{display:none !important;}',
      '</style>',
    ].join('');

    return '<!DOCTYPE html><html><head><meta charset="utf-8">' + base + '</head><body>' + content + '</body></html>';
  }

  onLoad() {
    this.disconnect();
    this.applyInlineImages();
    this.measure();
    const doc = this.frameDocument();
    if (doc) {
      // Dead links and Gmail-private images: a clear placeholder instead of the browser's broken icon.
      const showMissing = (img: HTMLImageElement) => {
        if (img.getAttribute('src') !== MISSING_IMAGE_SRC) img.setAttribute('src', MISSING_IMAGE_SRC);
      };
      doc.addEventListener('error', (event: Event) => {
        const target = event.target as HTMLImageElement;
        if (target && target.tagName === 'IMG') showMissing(target);
      }, true);
      doc.addEventListener('load', (event: Event) => {
        const target = event.target as HTMLImageElement;
        if (target && target.tagName === 'IMG') this.uncollapse(target);
      }, true);
      Array.from(doc.images).forEach((img) => {
        if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) showMissing(img);
        else if (img.complete) this.uncollapse(img);
      });
    }
    if (doc && doc.body && (window as any).ResizeObserver) {
      // Late-loading images change the height after `load`.
      this.resizeObserver = new (window as any).ResizeObserver(() => this.measure());
      this.resizeObserver.observe(doc.body);
    }
  }

  beforeDestroy() {
    this.disconnect();
  }

  // Gmail signatures put avatars/logos in table cells without a width: with `max-width:100%` the
  // cell shrinks in narrow frames and the image ends up a few pixels wide. Keep small images at
  // their real size (up to 160px); big ones still scale down.
  private uncollapse(img: HTMLImageElement) {
    // A width set by the email (attribute or inline style) is intentional: leave it alone.
    if (img.getAttribute('width') || img.style.width) return;
    if (!img.naturalWidth || img.width >= Math.min(img.naturalWidth, 160) - 1) return;
    img.style.minWidth = Math.min(img.naturalWidth, 160) + 'px';
  }

  private applyInlineImages() {
    const doc = this.frameDocument();
    if (!doc) return;
    Array.from(doc.querySelectorAll('img[data-hd-cid]')).forEach((img) => {
      const url = this.inlineUrls[img.getAttribute('data-hd-cid') || ''];
      if (url === undefined) return; // still loading
      img.setAttribute('src', url || MISSING_IMAGE_SRC);
      img.removeAttribute('data-hd-cid');
    });
  }

  private frameDocument(): Document | null {
    const frame = this.$refs.frame as HTMLIFrameElement | undefined;
    return frame && frame.contentDocument ? frame.contentDocument : null;
  }

  private measure() {
    const doc = this.frameDocument();
    if (!doc || !doc.documentElement) return;
    const content = Math.max(doc.documentElement.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
    this.height = this.maxHeight ? Math.min(content, this.maxHeight) : content;
    const clamped = !!this.maxHeight && content > this.maxHeight;
    if (clamped !== this.clamped) {
      this.clamped = clamped;
      this.$emit('clamped', clamped);
    }
  }

  @Watch('maxHeight')
  onMaxHeightChanged() {
    this.measure();
  }

  private disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
